import { Box, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import React, { ReactNode } from 'react'

const data = []
export const LargeBold: React.FC<{ children: ReactNode }> = ({ children }) => {
    return (
        <Typography
            fontSize={{ xs: '42px', sm: '45px' }}
            fontWeight={700}
            color="#23282B"
        >
            {children}
        </Typography>
    );
};

export const SmallBold: React.FC<{ children: ReactNode }> = ({ children }) => {
    return (
        <Typography fontSize="24px" fontWeight={700} color="#23282B">
            {children}
        </Typography>
    );
};

export const SmallRegular: React.FC<{ children: ReactNode }> = ({ children }) => {
    return (
        <Typography fontSize="18px" fontWeight={400} color="#23282B">
            {children}
        </Typography>
    );
};

export const Point: React.FC<{ children: ReactNode }> = ({ children }) => {
    return (
        <Typography
            fontSize="18px"
            fontWeight={400}
            color="#23282B"
            display="inline-flex"
            alignItems="flex-start"
            gap="8px"
            pl="8px"
        >
            <Box>•&nbsp;</Box>
            {children}
        </Typography>
    );
};

const GettingStarted = () =>   {
    return (
        <Box>
            <Box mb={{ xs: '36px', sm: '45px' }}>
                <LargeBold>Getting Started on Jobfactor</LargeBold>
            </Box>
            <Box mb="32px">
                <Box mb={{ xs: '20px', sm: '25px' }}>
                    <SmallBold>1. Creating an Account</SmallBold>
                </Box>
                <Box>
                    <SmallRegular>
                        You can create a new account with a valid email address
                        and a strong Password or simply sign up using your gmail
                        or LinkedIn Account. For account verification purposes,
                        we may send a unique code to your email or phone number,
                        check your spam folder if you are unable to see the code
                        in your inbox and ensure to adjust your setting to
                        ensure emails from Jobfactor always land in your inbox{' '}
                        <br />
                        Two types of account that can be created on Jobfactor:
                        <br />
                        <br />
                        <Point>Professional account for individuals</Point>
                        <br />
                        <Point>
                            Company account for companies seeking talents for
                            their organization.
                        </Point>
                        <br />
                        <br />
                        If you log out of your account, you can use the same
                        login credentials used in creating the account to sign
                        back into Jobfactor. Keep your credential safe and
                        secure and never share your password with anyone.
                        Jobfactor will never ask for your login password. <br />{' '}
                        If you encounter an error, please contact our technical
                        team for support at
                        <a
                            href="contact@jobfactor.com"
                            style={{
                                textDecoration: 'none',
                                display: 'inline',
                                color: '#05668D'
                            }}
                        >
                            contact@jobfactor.com
                        </a>
                        .
                    </SmallRegular>
                </Box>
            </Box>
            <Box mb="32px">
                <Box mb="25px">
                    <SmallBold>2. Posting an Achievement</SmallBold>
                </Box>
                <Box>
                    <SmallRegular>
                        Every milestone achieved, every new skill learned,
                        project completed or award and recognition at work is
                        worthy of celebration. Jobfactor provides you a platform
                        to highlight key professional accomplishments in real
                        time and give your professional community an opportunity
                        to validate those achievements through peer reviews.
                        These reviews/ratings not only boost your Jobfactor
                        score, they add to your professional credibility,
                        increasing your chance of getting hired.
                        <br />
                        <br />
                        You can post an achievement from the home page. Select
                        the type of achievement you are celebrating, provide
                        enough details that can be shared publicly about your
                        achievement. Please be mindful of your company’s
                        confidentiality policy. Do not share specific details
                        that your company may consider confidential. You may
                        also attach a non-confidential proof of achievement,
                        such as an image of a certification, an award, or a
                        photo of you and your team celebrating completion of a
                        project.
                        <br />
                        <br />
                        Next, identify skills acquired or demonstrated as a
                        result of this achievement; you may select up to a
                        maximum of 5 skills. Once you have identified
                        demonstrated skills, you can then invite a maximum of
                        five coworkers to rate your selected skills. These
                        individuals must be professionals within your Jobfactor
                        Community with firsthand knowledge of your activities
                        relating to the achievement you are celebrating and can
                        validate your achievements.
                        <br />
                        <br />
                        If these individuals are not currently on Jobfactor, you
                        can invite them to join your Network. Once they have
                        joined, you will be automatically connected and you will
                        be able to request for feedback on your accomplishments
                        from them. To aid you in this process, when you first
                        sign up on Jobfactor we recommend inviting your
                        coworkers to join your network.  It is part of our
                        Policy at Jobfactor for every member of our community to
                        have at least 10 professional connections before having
                        a Jobfactor score. These connections must be individuals
                        with whom you have real professional relationships both
                        past and present. This is beneficial for two reasons:
                        <br />
                        <br />
                        <Point>
                            Having connections with real world professional
                            relationships serves as a social proof that
                            increases community trust and gives you credibility
                            within the ecosystem.
                        </Point>
                        <br />
                        <Point>
                            You earn 250 points towards your Jobfactor Score.
                            The 250 basic points is awarded to all professional
                            users on Jobfactor with a complete Jobfactor profile
                            and at least 10 connections in their community.
                        </Point>
                        <br />
                        <br />
                        After you post your achievement and request review from
                        your connections, your demonstrated skills associated
                        with that achievement remains in unrated states until
                        your connections respond to your review request
                    </SmallRegular>
                </Box>
            </Box>
            <Box mb="32px">
                <Box mb="25px">
                    <SmallBold>3. Reviews</SmallBold>
                </Box>
                <Box>
                    <SmallRegular>
                        Everyone with a professional account on Jobfactor have a
                        unique opportunity to grow while helping others grow
                        through the reviews/ratings process. The peer review
                        process on Jobfactor is a key part of what make our
                        community unique. One of the core values of Jobfactor is
                        our keenly committed community (The peer review process
                        is designed specifically to strengthen the community).
                        How does the peer review process work?
                        <br />
                        <br />
                        <Point>
                            When a professional in your network shares an
                            achievement and request peer reviews, you will get a
                            notification about the details of the achievements
                            and the associated skills.
                        </Point>
                        <br />{' '}
                        <Point>
                            After reviewing the post, if you witness the
                            achievement and all the details are accurate to the
                            best of your knowledge, you can then accept to rate
                            the skills associated with it. You will be asked to
                            confirm that you witnessed the achievement, after
                            which you will rate the demonstrated skills based on
                            the Jobfactor competency ratings (Please note that
                            biased ratings can incur Jobfactor score deduction).
                        </Point>
                          <br />
                    </SmallRegular>
                </Box>
            </Box>
            <Box>
                <Box mb="25px">
                    <SmallBold>4. Profile</SmallBold>
                </Box>
                <Box>
                    <SmallRegular>
                        <Point>
                            About Me: Describe your professional background
                            succinctly
                        </Point>
                        <br />
                        <Point>
                            Add your qualification, education work history and
                            other relevant information. Be sure to fill all
                            field in the qualification forms as those are
                            critical data points we use in calculating your
                            Jobfactor score.
                        </Point>
                        <br />
                        <Point>
                            Resume: This is a carefully curated aspect of your
                            professional history and work achievements as well
                            as your skills that Jobfactor put together for you
                            from your profile, which can be exported into a PDF
                            format for your external use purposes.
                        </Point>
                    </SmallRegular>
                </Box>
            </Box>
           
        </Box>
    );
}

export default GettingStarted